import { useRef, useState, type ChangeEvent } from "react";
import { api, UnauthorizedError } from "@/api/client";
import type { ChatMessage } from "@/api/types";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { Bookmark, Star, ThumbsUp, ThumbsDown, Copy, Check, RotateCcw, FileOutput } from "lucide-react";

const DOC_FILL_EXT_RE = /\.(docx|pptx|xlsx)$/i;

export default function AnswerActions({
  msg,
  question,
  customer,
  onFeedback,
  onRegenerate,
  onBookmark,
  onDocFill,
}: {
  msg: ChatMessage;
  question?: string;
  customer?: string;
  onFeedback: (fb: 1 | -1) => void;
  onRegenerate?: () => void;
  onBookmark?: (bookmarkId: number | null) => void;
  // 양식 문서 작성(doc_fill): 이 답변 내용으로 선택한 양식(docx·pptx·xlsx)을 채운다.
  onDocFill?: (file: File) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bmBusy, setBmBusy] = useState(false);
  const docFillRef = useRef<HTMLInputElement>(null);

  const onPickTemplate = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // 같은 파일 재선택 허용
    if (!f) return;
    if (!DOC_FILL_EXT_RE.test(f.name)) {
      toast("양식은 docx·pptx·xlsx 파일만 지원합니다.", "error");
      return;
    }
    onDocFill?.(f);
  };

  const onToggleBookmark = async () => {
    if (bmBusy) return;
    setBmBusy(true);
    try {
      if (msg.bookmarkId) {
        await api.bookmarkDelete(msg.bookmarkId);
        onBookmark?.(null);
        toast("즐겨찾기에서 제거했습니다.", "success");
      } else {
        const r = await api.bookmarkAdd({
          query_id: msg.sessionId ?? "",
          question: question ?? "",
          answer: msg.text,
          customer: customer ?? "",
        });
        onBookmark?.(r.id);
        toast("즐겨찾기에 저장했습니다.", "success");
      }
    } catch (e) {
      toast(e instanceof UnauthorizedError ? "로그인이 필요합니다." : "처리에 실패했습니다.", "error");
    } finally {
      setBmBusy(false);
    }
  };

  const onSave = async () => {
    if (!msg.sessionId) return;
    setSaving(true);
    try {
      const r = await api.save(msg.sessionId);
      if (r.status === "saved") {
        toast(r.page_url ? "지식 저장소에 저장했습니다." : (r.message ?? "저장 완료"), "success");
      } else {
        toast(r.message ?? "저장하지 못했습니다.", "error");
      }
    } catch (e) {
      toast(e instanceof UnauthorizedError ? "로그인이 필요합니다." : "저장 실패", "error");
    } finally {
      setSaving(false);
    }
  };

  const onFb = async (fb: 1 | -1) => {
    if (!msg.sessionId) return;
    onFeedback(fb);
    // 👎 는 부족했던 점(선택)을 받아 KB 보강 신호로 함께 저장
    let comment = "";
    if (fb === -1) {
      comment = (window.prompt("어떤 점이 부족했나요? (선택 — 비워도 됩니다)") || "").trim();
    }
    try {
      await api.feedback(msg.sessionId, fb, comment);
      if (fb === -1 && comment) toast("의견 감사합니다. 개선에 반영하겠습니다.", "success");
    } catch {
      /* 표시는 유지, 서버 실패만 무시 */
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(msg.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast("복사에 실패했습니다.", "error");
    }
  };

  const btn = "flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-muted";
  const canAct = !!msg.sessionId;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      {canAct && (
        <button className={btn} onClick={onSave} disabled={saving}>
          <Bookmark className="h-3.5 w-3.5" /> 저장
        </button>
      )}
      {canAct && (
        <>
          <button
            className={cn(btn, msg.feedback === 1 && "text-primary")}
            onClick={() => onFb(1)}
            aria-pressed={msg.feedback === 1}
          >
            <ThumbsUp className="h-3.5 w-3.5" />
          </button>
          <button
            className={cn(btn, msg.feedback === -1 && "text-destructive")}
            onClick={() => onFb(-1)}
            aria-pressed={msg.feedback === -1}
          >
            <ThumbsDown className="h-3.5 w-3.5" />
          </button>
        </>
      )}
      <button
        className={cn(btn, msg.bookmarkId && "text-[#EB4E26]")}
        onClick={onToggleBookmark}
        disabled={bmBusy}
        aria-pressed={!!msg.bookmarkId}
        title="개인 즐겨찾기(나만 보기)"
      >
        <Star className={cn("h-3.5 w-3.5", msg.bookmarkId && "fill-current")} /> 즐겨찾기
      </button>
      <button className={btn} onClick={onCopy}>
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} 복사
      </button>
      {onRegenerate && (
        <button className={btn} onClick={onRegenerate} title="같은 질문 다시 답변">
          <RotateCcw className="h-3.5 w-3.5" /> 재생성
        </button>
      )}
      {canAct && onDocFill && (
        <>
          <button
            className={btn}
            onClick={() => docFillRef.current?.click()}
            title="이 답변 내용으로 양식(docx·pptx·xlsx)의 빈칸을 채워 같은 형식의 문서를 만듭니다"
          >
            <FileOutput className="h-3.5 w-3.5" /> 양식으로 문서 만들기
          </button>
          <input
            ref={docFillRef}
            type="file"
            className="hidden"
            accept=".docx,.pptx,.xlsx"
            onChange={onPickTemplate}
          />
        </>
      )}
    </div>
  );
}
